// What this browser can record, decided once, synchronously and without a
// permission prompt — at hook construction, not on click, so a screen can
// show "cannot record here" or an adult-facing microphone notice before the
// child's first tap. docs/plan/speech-transcription-package.md § "Capability
// and mime negotiation".
//
// The globals are read in one place (`readCaptureEnvironment`) and the
// decision is a pure function of what was read, so the decision runs under
// the node test environment, which has neither `navigator.mediaDevices` nor
// `MediaRecorder`.
import type { CaptureError } from "../contract";

export type UnsupportedReason = Extract<
  CaptureError,
  { kind: "unsupported" }
>["reason"];

export type Capability =
  // `mime` is the candidate to request; what the recorder actually produces
  // is read back after `start()` and may differ (./driver.ts).
  | { readonly tag: "supported"; readonly mime: string }
  | { readonly tag: "unsupported"; readonly reason: UnsupportedReason };

// First accepted wins. WebM/Opus before MP4/AAC so Chromium and Firefox take
// Opus; Safari is expected to refuse both WebM entries and take MP4
// (unverified per browser — the capture README's browser matrix records what
// each actually reports). Bare types follow their codec-qualified forms for
// implementations that reject any `codecs` parameter.
export const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/ogg;codecs=opus",
] as const;

export interface CaptureEnvironment {
  readonly isSecureContext: boolean;
  readonly hasGetUserMedia: boolean;
  // `MediaRecorder.isTypeSupported`, or null when there is no usable
  // `MediaRecorder` — absent, as on Safari 14.0 (BROWSER_FLOOR's `safari14`),
  // or present without the static.
  readonly isTypeSupported: ((mime: string) => boolean) | null;
}

export const negotiateCapability = (env: CaptureEnvironment): Capability => {
  // getUserMedia is only exposed on secure origins (localhost counts), so an
  // insecure page would otherwise be misreported as `no-media-devices`.
  if (!env.isSecureContext)
    return { tag: "unsupported", reason: "insecure-context" };
  if (!env.hasGetUserMedia)
    return { tag: "unsupported", reason: "no-media-devices" };
  const isTypeSupported = env.isTypeSupported;
  if (isTypeSupported === null)
    return { tag: "unsupported", reason: "no-media-recorder" };
  const mime = MIME_CANDIDATES.find((m) => isTypeSupported(m));
  return mime === undefined
    ? { tag: "unsupported", reason: "no-supported-mime" }
    : { tag: "supported", mime };
};

// Every global lookup is guarded, because this runs during render and a
// prerendered page renders it where there is no `window` at all; such a page
// reads as `insecure-context` and must compute the capability client-side.
export const readCaptureEnvironment = (): CaptureEnvironment => {
  const recorder =
    typeof MediaRecorder === "undefined" ? undefined : MediaRecorder;
  return {
    isSecureContext:
      typeof window !== "undefined" && window.isSecureContext === true,
    hasGetUserMedia:
      typeof navigator !== "undefined" &&
      typeof navigator.mediaDevices?.getUserMedia === "function",
    isTypeSupported:
      typeof recorder?.isTypeSupported === "function"
        ? (mime) => recorder.isTypeSupported(mime)
        : null,
  };
};

export const detectCapability = (): Capability =>
  negotiateCapability(readCaptureEnvironment());
